import { sv } from "date-fns/locale";
import { format } from "date-fns";
import { useState,useEffect } from "react";
import { Badge } from "../Components/Badge";
import { Button } from "../Components/Button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../Components/Card";
import { Icon } from "../Icons/Icon";
import { AdminIcon } from "../Icons/AdminIcon";
import { useLocation, useNavigate } from "react-router-dom";
import { WrappedDayPicker } from "../Components/WrappedDayPicker";
import './Boking.css'
import FadeIn from "../Animations/FadeIn";
import { DataService } from "../DataService/DataService";
import { useCart } from "../DataService/CartContext";
import { useUser } from "../DataService/UserContext";
import { apiGet } from "../DataService/apiClient";
import { SlotType } from "../Types/BookingServiceTypes";
import { CartItem } from "../Types/CartTypes";
import { IncludedCallSource } from "../Types/ClientOfferTypes";
import ConsultingPricingService, { ConsultingPricingPublicDto, calculateConsultingQuote } from "../DataService/ConsultingPricingService";
import { GlobalObjectMapping } from "../Utils/GlobalObjectMapping";
import Loader from "../Components/Loader";
import { UserCourseListItem } from "../Types/UserCourseTypes";
export const Booking = () => {
const navigate = useNavigate();
  const location = useLocation();
  const { addItem } = useCart();
  const { user } = useUser();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlots, setSelectedSlots] = useState</*Array<{id: string, date: string, time: string}>*/SlotType[]>([]);
  const [availableSlots,setAvailableSlots] = useState<SlotType[]>([]);
  const [availableDates,setAvailabledatesForDatePicker] = useState<Date[]>([]);
  const [loading,setIsLoading] = useState<boolean>(false);
  const [accessItems, setAccessItems] = useState<UserCourseListItem[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [bookingMode, setBookingMode] = useState<"paid" | "included" | "intro">("paid");
  const [includedSessionSource, setIncludedSessionSource] = useState<IncludedCallSource>("grow-premium");
  const [pricingData, setPricingData] = useState<ConsultingPricingPublicDto | null>(null);

  useEffect(() => {
    const stateBookingMode = (location.state as { bookingMode?: "paid" | "included" | "intro" } | null)?.bookingMode;
    const queryMode = new URLSearchParams(location.search).get("mode");
    let nextMode: "paid" | "included" | "intro" | null = null;

    if (stateBookingMode === "intro" || stateBookingMode === "included" || stateBookingMode === "paid") {
      nextMode = stateBookingMode;
    } else if (queryMode === "intro" || queryMode === "included" || queryMode === "paid") {
      nextMode = queryMode as "paid" | "included" | "intro";
    }

    if (nextMode) {
      setBookingMode(nextMode);
    }
  }, [location.search, location.state]);
  useEffect(() => {
    setIsLoading(true);
    DataService.BookingService.GetAvailableTimeSlots()
    .then(async(res) => {
      if(res){
       const availableSlotsBuff = await GlobalObjectMapping.MapToTimeSlots(res);
        const uniqueSlots = availableSlotsBuff.filter((slot, index, arr) => arr.findIndex(x => x.id === slot.id) === index);
        const availableDatesForPicker = Array.from(new Set(uniqueSlots.map(x => x.date))).map(date => new Date(date));
        setAvailabledatesForDatePicker(availableDatesForPicker);
        setAvailableSlots(uniqueSlots);
       }
    })
    .catch((err) => {
      console.log('Något gick fel vid init',err);
    }).finally(() => {
      setIsLoading(false);
  })
  },[])

  useEffect(() => {
    const loadAccess = async () => {
      if (!user) {
        setAccessItems([]);
        return;
      }

      setAccessLoading(true);
      try {
        const myCourses = await apiGet<UserCourseListItem[]>("/UserCourse/my-courses");
        setAccessItems(myCourses ?? []);
      } catch {
        setAccessItems([]);
      } finally {
        setAccessLoading(false);
      }
    };

    void loadAccess();
  }, [user]);

  useEffect(() => {
    let mounted = true;

    const loadPricing = async () => {
      const data = await ConsultingPricingService.getPublic();
      if (mounted) setPricingData(data);
    };

    void loadPricing();

    return () => {
      mounted = false;
    };
  }, []);

  const hasPremiumAccess = Boolean(
    user?.membershipPlanKey === "premium" && accessItems.some(item => String(item.accessType ?? "").toLowerCase() === "membership")
  );
  const hasRotAccess = accessItems.some(item => String(item.accessType ?? "").toLowerCase() === "program");
  const canUseIncludedBooking = hasPremiumAccess || hasRotAccess;
  const isLoggedIn = Boolean(user);
  const isMember = Boolean(user && user.roles.some(role => ["member", "courseowner"].includes(role.toLowerCase())));
  const pricingQuote = calculateConsultingQuote(
    pricingData?.config.baseSessionPrice ?? 700,
    pricingData?.rules ?? [],
    selectedSlots.length > 0 ? selectedSlots.length : 1
  );

  useEffect(() => {
    if (bookingMode === "included" && !canUseIncludedBooking) {
      setBookingMode("paid");
    }
  }, [bookingMode, canUseIncludedBooking]);

  
  const HandeleDayPickerCallback = async(value:Date):Promise<void> => {
    setSelectedDate(value);
  }
  // Hämta tillgängliga tider för valt datum
  const getAvailableSlotsForDate = (date: Date | undefined) => {
    if (!date) return [];
    const dateStr = format(date, "yyyy-MM-dd");
    return availableSlots.filter(slot => slot.date === dateStr);
  };

  useEffect(() => {
    if (bookingMode === "intro" && selectedSlots.length > 1) {
      setSelectedSlots(prev => prev.slice(0, 1));
    }
  }, [bookingMode, selectedSlots]);

  const toggleSlot = (slot: SlotType) => {
    if (bookingMode === "intro") {
      setSelectedSlots(prev => (prev.some(s => s.id === slot.id) ? [] : [slot]));
      return;
    }

    if (bookingMode === "included") {
      if (!canUseIncludedBooking) return;
      setSelectedSlots(prev => (prev.some(s => s.id === slot.id) ? [] : [slot]));
      return;
    }

    setSelectedSlots(prev => {
      const exists = prev.find(s => s.id === slot.id);
      if (exists) {
        return prev.filter(s => s.id !== slot.id);
      } else {
        return [...prev, { id: slot.id, date: slot.date, time: slot.time,dayName:slot.dayName,available:false,tableId:slot.tableId }];
      }
    });
  };

  const removeSlot = (slotId: string) => {
    setSelectedSlots(prev => prev.filter(s => s.id !== slotId));
  };

  const handleBooking = () => {
    if (selectedSlots.length === 0) {
      return;
    }

    const appliedQuote = calculateConsultingQuote(
      pricingData?.config.baseSessionPrice ?? 700,
      pricingData?.rules ?? [],
      selectedSlots.length
    );
    const totalAmount = bookingMode === "paid" ? appliedQuote.totalAmount : 0;
    const title = bookingMode === "intro"
      ? "Introduktionssamtal"
      : bookingMode === "included"
        ? "Inkluderad bokning av samtal"
        : "Bokning av samtal";
      const description = bookingMode === "intro"
      ? "Ett kostnadsfritt introduktionssamtal"
      : bookingMode === "included"
        ? `${selectedSlots.length} valt(a) tid(er) via ${includedSessionSource === "grow-premium" ? "GROW Premium" : "ROT-programmet"}`
        : `${selectedSlots.length} valt(a) tid(er)`;
    const pricingSnapshot = bookingMode === "paid" ? {
      serviceKey: 'consulting',
      baseSessionPrice: appliedQuote.baseSessionPrice,
      sessionCount: selectedSlots.length,
      subtotal: appliedQuote.subtotal,
      discountAmount: appliedQuote.discountAmount,
      totalAmount: appliedQuote.totalAmount,
      appliedRule: appliedQuote.appliedRule,
      capturedAtUtc: new Date().toISOString(),
    } : null;
    const item: CartItem = {
      id: `booking-${bookingMode}-${includedSessionSource}-${selectedSlots.map(slot => slot.id).join('-')}`,
      type: 'booking',
      referenceId: selectedSlots.map(slot => slot.id).join('-'),
      title,
      description,
      price: totalAmount,
      quantity: 1,
      metadata: {
        slots: selectedSlots.map(slot => `${slot.date} ${slot.time}`),
        slotCount: selectedSlots.length,
        bookingMode,
        ...(pricingSnapshot ? { consultingPricingSnapshotJson: JSON.stringify(pricingSnapshot) } : {}),
        ...(bookingMode === "included" ? { includedSessionSource } : {}),
      }
    };

    addItem(item);
    navigate('/cart');
  };

  const selectedSlotsForDate = getAvailableSlotsForDate(selectedDate);


  return (
    <div className="booking">
      <section className="booking-hero">
        <div className="booking-shell booking-hero-grid">
          <FadeIn>
            <div className="booking-hero-copy">
              <p className="booking-hero-kicker">Enskilda samtal – stöd när du behöver det som mest</p>
              <h1 className="booking-hero-title">För dig som vill ha stöd här och nu – utan att gå ett program eller medlemskap.</h1>
              <p className="booking-hero-text">Ibland är det inte rätt läge för en större process. Ibland behöver du ett tryggt rum för att sortera tankar, förstå vad som händer och få tydlig vägledning framåt. Enskilda samtal passar dig som står i något som skaver just nu – exempelvis stress, oro, relationer, livsbeslut, självkritik, gränssättning eller återkommande mönster du vill förstå bättre. Samtalen är strukturerade men alltid anpassade efter dig och din situation. Du får både reflektion, förståelse och konkreta perspektiv att ta med dig vidare. Du kan boka ett enstaka samtal eller flera vid behov – helt utan krav på program eller medlemskap.</p>
              <div className="booking-hero-badge-row">
                <Badge variant="secondary" className="booking-hero-badge">60 minuter</Badge>
                  <Badge variant="secondary" className="booking-hero-badge">895 kr / 60 minuter</Badge>
                <Badge variant="secondary" className="booking-hero-badge">Online</Badge>
              </div>
            </div>
          </FadeIn>

          <FadeIn className="booking-hero-visual">
            <div className="booking-hero-image-card">
              <img src="/images/chair.png" alt="Sandra" className="booking-hero-image" />
            </div>
            <div className="booking-hero-note-card">
              <p className="booking-hero-note-kicker">Det här får du</p>
              <h2>✔ 60 minuters samtal online</h2>
              <p>✔ Professionell vägledning anpassad efter din situation</p>
              <p>✔ Stöd att förstå mönster och sortera det som känns svårt</p>
              <p>✔ Ett tryggt och tydligt samtal med riktning framåt</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Hero Section */}
      {/* Bento Grid - Service Overview */}
      <section className="booking-service-section">
          <Card disableHover className="booking-service-section-mode-card">
          <CardHeader className="booking-service-section-calendar-header-gradient">
              <CardTitle className="booking-service-section-calendar-header-text">
                <Icon.CheckMarkWide fill="grey" width={28} height={28} />
                Boka här
              </CardTitle>
              <CardDescription>
                Enskilda samtal online, helt utan koppling till något program eller medlemskap.
              </CardDescription>
          </CardHeader>
          <CardContent className="booking-service-section-calendar-content-container booking-service-section-mode-content">
            <div className="booking-service-section-mode-toggle-row">
              <Button
                text="Enskilda samtal"
                variant={bookingMode === "paid" ? "primary" : "secondary"}
                onClick={() => setBookingMode("paid")}
                className="booking-service-section-mode-toggle-button"
              />
              <Button
                text="Inkluderad session"
                variant={bookingMode === "included" ? "primary" : "secondary"}
                onClick={() => canUseIncludedBooking ? setBookingMode("included") : setBookingMode("paid")}
                className="booking-service-section-mode-toggle-button"
                disabled={accessLoading || !canUseIncludedBooking}
              />
              <Button
                text="Introduktionssamtal"
                variant={bookingMode === "intro" ? "primary" : "secondary"}
                onClick={() => setBookingMode("intro")}
                className="booking-service-section-mode-toggle-button"
              />
            </div>

            {bookingMode === "included" && (
              <label className="booking-service-section-mode-select-label">
                <span>Inkluderad från</span>
                <select
                  value={includedSessionSource}
                  onChange={(e) => setIncludedSessionSource(e.target.value as IncludedCallSource)}
                  className="booking-service-section-mode-select"
                >
                  <option value="grow-premium">GROW Premium</option>
                  <option value="rot-program">ROT-programmet</option>
                </select>
              </label>
            )}

            <p className="booking-service-section-mode-note">
              {bookingMode === "intro"
                ? "Den här tiden läggs som ett kostnadsfritt introduktionssamtal. Endast ett per person."
                : bookingMode === "included"
                  ? "Den här tiden läggs som en inkluderad session och får 0 kr i korgen."
                  : "Den här tiden behandlas som ett vanligt enskilt samtal."}
            </p>
            {bookingMode === "included" && !canUseIncludedBooking && (
              <p className="booking-service-section-mode-note" style={{ color: "#9f5255" }}>
                Inkluderade tider visas bara när du har aktiv GROW Premium eller ROT-access.
              </p>
            )}
          </CardContent>
        </Card>

        {/* <div className="booking-service-section-header-container">
          <h2 className="booking-service-section-header-text">
            Våra samtalsformer
          </h2>
          <p className="booking-service-section-header-paragraph">
            Välj den form av samtal som passar dig bäst
          </p>
        </div> */}

        {/* Bento Grid Layout */}
        {/* <div className="booking-service-section-grid">
          {bentoContent.map((item, index) => (
            <FadeIn>
            <Card key={index} className="booking-service-section-card">
              <div className={`booking-service-section-card-header-gradient`}></div>
              <CardHeader>
                <div className={`booking-service-section-card-header-gradient-icon-container`}>
                  {item.icon}
                </div>
                <CardTitle >{item.title}</CardTitle>
                <CardDescription className="text-gray-600">{item.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {item.features.map((feature, idx) => (
                    <li key={idx} className="booking-service-section-card-content-container">
                      <Icon.CheckMarkCircle  />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            </FadeIn>
          ))}
        </div> */}

        {/* Upcoming Bookings - Only for Members behöver också hämta in data mot användar id och hämta bokningar */}
        {isLoggedIn && isMember && (
          <Card className="mb-16 border-2 border-[#E16A54]/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#7C444F]">
                <AdminIcon.SimpleCalendar />
                Dina kommande bokningar
              </CardTitle>
              <CardDescription>Schemalagda samtalstillfällen</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <p>Du har inga bokningar ännu</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Booking Calendar */}
        {loading && <Loader loaderSize='XLARGE' bgColor="black"/>}
        {!loading &&
          (
            <div className="booking-service-section-calendar">
          <div style={{textAlign:'center',marginBottom:8}}>
            <h2 className="booking-service-section-header-text ">
              När vill du ses?
            </h2>
              <p className="booking-service-section-header-paragraph">
              Välj en dag och se tider som finns tillgängliga.
            </p>
          </div>

          <div className="booking-service-section-calendar-grid">
            {/* Calendar */}
            <Card disableHover className="booking-service-section-calendar-card">
              <CardHeader className="booking-service-section-calendar-header-gradient">
                <CardTitle className="booking-service-section-calendar-header-text">
                  <AdminIcon.SimpleCalendar fill="grey"/>
                  Välj datum
                </CardTitle>
              </CardHeader>
              <CardContent className="booking-service-section-calendar-content-container">
                <WrappedDayPicker availableDates={availableDates} selectedDate={selectedDate} selectedDateCallBack={HandeleDayPickerCallback}/>
                {/* <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => {
                    const today = startOfToday();
                    const maxDate = addDays(today, 14);
                    return date < today || date > maxDate || !hasAvailableSlots(date);
                  }}
                  locale={sv}
                  className={cn("pointer-events-auto")}
                  modifiers={{
                    available: (date) => hasAvailableSlots(date)
                  }}
                  modifiersClassNames={{
                    available: "bg-[#E16A54]/10 font-semibold"
                  }}
                /> */}
              </CardContent>
            </Card>

            {/* Available Times */}
            <Card disableHover className="booking-service-section-calendar-card">
              <CardHeader className="booking-service-section-calendar-header-gradient">
                <CardTitle className="booking-service-section-calendar-header-text">
                  <AdminIcon.Clock fill='grey'/>
                  Tider som finns just nu
                </CardTitle>
                <CardDescription>
                  {selectedDate 
                    ? format(selectedDate, "EEEE d MMMM yyyy", { locale: sv })
                    : "Välj en dag för att se tider"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {!selectedDate ? (
                  <div className="booking-service-section-calendar-available-text">
                    <AdminIcon.SimpleCalendar />
                    <p>Välj en dag för att se tider</p>
                  </div>
                ) : selectedSlotsForDate.length === 0 ? (
                  <div className="booking-service-section-calendar-available-text">
                    <AdminIcon.Clock fill='grey'/>
                    <p>Inga lediga tider detta datum</p>
                  </div>
                ) : (
                  <div className="booking-service-section-calendar-available-container">
                    {selectedSlotsForDate.map((slot) => {
                      const isSelected = selectedSlots.some(s => s.id === slot.id);
                      return (
                                                <button
                          key={slot.id}
                          onClick={() => toggleSlot(slot)}
                          className={`booking-service-section-calendar-available-button ${isSelected ? 'selected' : 'inactive'}`}
                        >
                          <div className="booking-service-section-calendar-available-icon-container">
                            <AdminIcon.Clock width={isSelected ? 30.6 : 30} fill={isSelected ? 'white' : 'black'} />
                            {slot.time}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
          )
        }
        

      </section>

      {/* Fixed Footer with Selected Slots */}
      {selectedSlots.length > 0 && (
        <div className="booking-info-footer">
          <div className="booking-info-footer-container">
            <div className="booking-info-footer-content-wrapper">
              <div className="booking-info-footer-badge-container">
                <p className="booking-info-footer-badge-paragraph">
                  Valda tider ({selectedSlots.length})
                </p>
                <div className="booking-info-footer-badge-slot-container">
                  {selectedSlots.map((slot) => (
                    <Badge 
                      key={slot.id} 
                      variant="secondary"
                      className="booking-info-footer-badge-badge"
                    >
                      <span className="booking-info-footer-badge-slot-time-text">
                        {format(new Date(slot.date), "d MMM", { locale: sv })} kl {slot.time}
                      </span>
                      <button
                        onClick={() => removeSlot(slot.id)}
                        className="booking-info-footer-badge-slot-button"
                      >
                        <Icon.CheckMarkCross />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="booking-info-footer-badge-slot-payment-container">
                <div className="booking-info-footer-badge-slot-payment-cost-container">
                  <p className="booking-info-footer-badge-slot-payment-cost-total">Totalt</p>
                   <p className="booking-info-footer-badge-slot-payment-cost-value text-2xl font-bold">
                    {bookingMode === "paid" ? `${pricingQuote.totalAmount} kr` : '0 kr'}
                   </p>
                   {bookingMode === "paid" && pricingQuote.appliedRule && (
                     <p className="booking-info-footer-badge-slot-payment-cost-discount">
                        Vid bokning av paket på 5 samtal eller fler får du 10% mängdrabatt. Rabatt: {pricingQuote.appliedRule.displayText} sparar {pricingQuote.discountAmount.toLocaleString('sv-SE')} kr
                     </p>
                   )}
                </div>
                <Button 
                style={{cursor:'pointer'}}
                  text="Boka tid"
                  onClick={handleBooking}
                  className="submit-button"
                />
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};
